import type { LandingCopy } from "./landingParents";

// Copy for the home-reno / DIY Meta-ads landing page
// (/reno-recus-retours, /en/home-reno-receipts-returns). Targets people doing
// small home repairs and renovations — the crowd that buys a pile of materials
// and inevitably returns the leftovers, the wrong size, or the part that broke.
// Single source of truth — consumed by the landing components, the runtime SEO
// head and the build-time prerender head.

export const landingReno: Record<"fr" | "en", LandingCopy> = {
  fr: {
    meta: {
      title: "ReFound — Gardez vos reçus de réno | Retours faciles après les travaux",
      description:
        "Petits travaux ou grosse réno? ReFound garde tous vos reçus de quincaillerie au même endroit. Retournez le surplus, la mauvaise grandeur ou une pièce brisée en quelques secondes. Gratuit sur iPhone et Android.",
    },
    hero: {
      title: "Tes reçus de réno, jamais perdus",
      subtitle:
        "Scannez vos reçus de quincaillerie une fois. Retrouvez le bon en 2 secondes pour retourner le surplus, échanger la mauvaise grandeur ou faire jouer la garantie d'un outil.",
      cta: "Télécharger gratuitement",
      reassurance: "Gratuit · Installée en 30 secondes · Pensé pour les projets de maison",
      mockupAlt: "L'application ReFound affichant un reçu de quincaillerie avec les détails",
      familyAlt: "Un bricoleur dans son garage, téléphone en main devant ses matériaux de réno",
    },
    rating: { value: "5,0", label: "sur l'App Store" },
    ctaShort: "Télécharger — gratuit",
    stickyHint: "ReFound, gratuit sur iPhone et Android",
    midCta: {
      text: "Le prochain retour à la quincaillerie sera tellement plus simple.",
      cta: "Télécharger gratuitement",
    },
    benefits: {
      title: "Pourquoi télécharger ReFound?",
      items: [
        {
          title: "Tous vos reçus de réno au même endroit",
          text: "Peinture, bois, vis, luminaires… Fini les reçus froissés au fond du coffre du camion.",
        },
        {
          title: "Retournez le surplus sans stress",
          text: "Le bon reçu, prêt à présenter au comptoir pour retourner ce qui n'a pas servi à la fin des travaux.",
        },
        {
          title: "Gardez les garanties de vos outils",
          text: "La preuve d'achat et la garantie de la perceuse ou de la scie, accessibles le jour où ça lâche.",
        },
      ],
    },
    testimonials: {
      title: "Des bricoleurs qui ne perdent plus leurs reçus",
      items: [
        {
          title: "Ultra pratique",
          quote:
            "L'application est facile à utiliser et est super pratique dans la vie de tout les jours pour gérer les reçus. 👌",
          name: "Emilie.chbno",
          detail: "avis App Store",
        },
        {
          title: "Un outil efficace pour gérer ses achats",
          quote:
            "J'adore cette application! Elle me simplifie la vie, surtout pour retrouver facilement un reçu ou faire un retour sans stress. Tout est fluide, rapide et super bien organisé.",
          name: "CharboMa",
          detail: "avis App Store",
        },
        {
          title: "Application solide",
          quote:
            "Application très pratique pour scanner les reçus et simplifier les retours, rendant la gestion des achats rapide et sans stress.",
          name: "Servant922",
          detail: "avis App Store",
        },
      ],
    },
    how: {
      title: "Comment ça marche",
      steps: [
        "Scannez vos reçus de quincaillerie",
        "ReFound les organise et les enrichit",
        "Retrouvez-les pour retourner le surplus, échanger ou vérifier une garantie",
      ],
    },
    secondCta: {
      title: "Moins de reçus perdus. Des retours de réno sans casse-tête.",
      cta: "Télécharger ReFound",
    },
    faq: {
      title: "Questions fréquentes",
      items: [
        {
          q: "Est-ce que ReFound est utile pour les projets de rénovation?",
          a: "Oui. On accumule vite des dizaines de reçus pendant une réno. ReFound les garde tous au même endroit pour retrouver le bon quand vient le temps de retourner du surplus ou de faire jouer une garantie.",
        },
        {
          q: "Est-ce que je peux l'utiliser pour un retour en magasin?",
          a: "Oui. Vous retrouvez le bon reçu, affichez son image et le code-barres quand il est disponible pour le présenter au comptoir. L'acceptation du retour dépend de la politique du marchand.",
        },
        {
          q: "Est-ce que ça garde les garanties de mes outils?",
          a: "Oui. Gardez la preuve d'achat et les détails de garantie de vos outils au même endroit, prêts le jour où un appareil vous lâche.",
        },
        {
          q: "Est-ce que l'app est gratuite?",
          a: "Oui. ReFound est gratuit à télécharger et les fonctions essentielles sont gratuites.",
        },
      ],
    },
    footer: {
      tagline: "Vos reçus, au même endroit.",
      privacy: "Confidentialité",
      terms: "Conditions",
      support: "Aide",
      rights: "Tous droits réservés.",
    },
    switchLabel: "EN",
    switchTitle: "Switch to English",
  },
  en: {
    meta: {
      title: "ReFound — Keep your reno receipts | Easy returns after the job",
      description:
        "Small repair or big renovation? ReFound keeps all your hardware-store receipts in one place. Return the leftovers, the wrong size or a broken part in seconds. Free on iPhone and Android.",
    },
    hero: {
      title: "Your reno receipts, never lost",
      subtitle:
        "Scan your hardware-store receipts once. Find the right one in 2 seconds to return leftovers, exchange the wrong size or claim a tool's warranty.",
      cta: "Download for free",
      reassurance: "Free · Installed in 30 seconds · Built for home projects",
      mockupAlt: "The ReFound app showing a hardware-store receipt with product details",
      familyAlt: "A DIYer in their garage, phone in hand next to their reno materials",
    },
    rating: { value: "5.0", label: "on the App Store" },
    ctaShort: "Download — free",
    stickyHint: "ReFound, free on iPhone and Android",
    midCta: {
      text: "Your next hardware-store return will be so much easier.",
      cta: "Download for free",
    },
    benefits: {
      title: "Why download ReFound?",
      items: [
        {
          title: "All your reno receipts in one place",
          text: "Paint, lumber, screws, fixtures… No more crumpled receipts at the bottom of the truck.",
        },
        {
          title: "Return leftovers stress-free",
          text: "The right receipt, ready to show at the counter to return whatever you didn't use once the job's done.",
        },
        {
          title: "Keep your tool warranties",
          text: "The proof of purchase and warranty for that drill or saw, ready the day it gives out.",
        },
      ],
    },
    testimonials: {
      title: "DIYers who stopped losing their receipts",
      items: [
        {
          title: "Very useful app",
          quote:
            "Great app to get more organized with your store receipts and facilitate searching through them and also facilitating items returns.",
          name: "Ari.chbno",
          detail: "App Store review",
        },
        {
          title: "Game changer for organization!",
          quote:
            "This app is a total game changer! The smart scanning technology makes multitasking so much easier by handling all my receipts in seconds. It's the perfect tool for anyone looking to level up their organization.",
          name: "Charelixo",
          detail: "App Store review",
        },
        {
          title: "Best app of it kind",
          quote:
            "Best app to scan receipts and to help return product before the warranty is over! Great interface and very easy to use!",
          name: "MarsClement",
          detail: "App Store review",
        },
      ],
    },
    how: {
      title: "How it works",
      steps: [
        "Scan your hardware-store receipts",
        "ReFound organizes and enriches them",
        "Find them to return leftovers, exchange or check a warranty",
      ],
    },
    secondCta: {
      title: "Fewer lost receipts. Reno returns without the headache.",
      cta: "Download ReFound",
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          q: "Is ReFound useful for renovation projects?",
          a: "Yes. You pile up dozens of receipts during a reno. ReFound keeps them all in one place so you can find the right one when it's time to return leftovers or claim a warranty.",
        },
        {
          q: "Can I use it for an in-store return?",
          a: "Yes. You find the right receipt, show its image and the barcode when available to present at the counter. Whether the return is accepted depends on the retailer's policy.",
        },
        {
          q: "Does it keep my tool warranties?",
          a: "Yes. Keep the proof of purchase and warranty details for your tools in one place, ready the day a device gives out.",
        },
        {
          q: "Is the app free?",
          a: "Yes. ReFound is free to download and the core features are free.",
        },
      ],
    },
    footer: {
      tagline: "Your receipts, in one place.",
      privacy: "Privacy",
      terms: "Terms",
      support: "Help",
      rights: "All rights reserved.",
    },
    switchLabel: "FR",
    switchTitle: "Passer au français",
  },
};
